import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Empty, message, Modal, Pagination, Tag } from 'antd';
import styled from 'styled-components/macro';
import * as QueryString from 'query-string';
import { DeleteFilled, PlusOutlined } from '@ant-design/icons';
import { useLocation } from 'react-router';
import PolicyBuilderModal from './PolicyBuilderModal';
import {
    Policy,
    PolicyUpdateInput,
    PolicyState,
    PolicyType,
    Maybe,
    ResourceFilterInput,
    PolicyMatchFilter,
    PolicyMatchFilterInput,
    PolicyMatchCriterionInput,
    EntityType,
} from '../../../types.generated';
import { useAppConfig } from '../../useAppConfig';
import PolicyDetailsModal from './PolicyDetailsModal';
import {
    useCreatePolicyMutation,
    useDeletePolicyMutation,
    useListPoliciesQuery,
    useUpdatePolicyMutation,
} from '../../../graphql/policy.generated';
import { Message } from '../../shared/Message';
import { EMPTY_POLICY } from './policyUtils';
import TabToolbar from '../../entity/shared/components/styled/TabToolbar';
import { StyledTable } from '../../entity/shared/components/styled/StyledTable';
import AvatarsGroup from '../AvatarsGroup';
import { useEntityRegistry } from '../../useEntityRegistry';
import { SearchBar } from '../../search/SearchBar';
import { scrollToTop } from '../../shared/searchUtils';
import analytics, { EventType } from '../../analytics';
import { ComponentSpinner } from '../../shared/ComponentSpinner';
import { useTranslation } from '../../../i18n/useTranslation';

const SourceContainer = styled.div``;

const PaginationContainer = styled.div`
    display: flex;
    justify-content: center;
`;

const PolicyName = styled.span`
    cursor: pointer;
`;

const PoliciesType = styled(Tag)`
    && {
        border-radius: 2px !important;
        border: 1px solid #8f9094;
        color: #8f9094 !important;
        background-color: transparent;
    }
`;

const ActorTag = styled(Tag)`
    && {
        display: inline-block;
        text-align: center;
    }
`;

const ActionButtonContainer = styled.div`
    display: flex;
    justify-content: right;
`;

const EditPolicyButton = styled(Button)`
    margin-right: 8px;
`;

const ToggleActiveButton = styled(Button)`
    min-width: 91px;

    span {
        width: 100%;
    }
`;

const StateTag = styled(Tag)`
    && {
        color: #ffffff !important;
        text-transform: capitalize;
    }

    &.green {
        background-color: #86b62c;
    }

    &.red {
        background-color: #d96758;
    }
`;

const PageContainer = styled.span`
    width: 100%;
`;

const TableActionHeader = styled.div`
    text-align: center;
`;

const DEFAULT_PAGE_SIZE = 10;

type PrivilegeOptionType = {
    type?: string;
    name?: Maybe<string>;
};

const toFilterInput = (filter: PolicyMatchFilter): PolicyMatchFilterInput => {
    return {
        criteria: filter.criteria?.map((criterion): PolicyMatchCriterionInput => {
            return {
                field: criterion.field,
                values: criterion.values.map((criterionValue) => criterionValue.value),
                condition: criterion.condition,
            };
        }),
    };
};

const toPolicyInput = (policy: Omit<Policy, 'urn'>): PolicyUpdateInput => {
    let policyInput: PolicyUpdateInput = {
        type: policy.type,
        name: policy.name,
        state: policy.state,
        description: policy.description,
        privileges: policy.privileges,
        actors: {
            users: policy.actors.users,
            groups: policy.actors.groups,
            allUsers: policy.actors.allUsers,
            allGroups: policy.actors.allGroups,
            resourceOwners: policy.actors.resourceOwners,
            resourceOwnersTypes: policy.actors.resourceOwnersTypes,
        },
    };
    if (policy.resources !== null && policy.resources !== undefined) {
        let resourceFilter: ResourceFilterInput = {
            type: policy.resources.type,
            resources: policy.resources.resources,
            allResources: policy.resources.allResources,
        };
        if (policy.resources.filter) {
            resourceFilter = { ...resourceFilter, filter: toFilterInput(policy.resources.filter) };
        }
        // Add the resource filters.
        policyInput = {
            ...policyInput,
            resources: resourceFilter,
        };
    }
    return policyInput;
};

// TODO: Cleanup the styling.
export const ManagePolicies = () => {
    const { t } = useTranslation();
    const entityRegistry = useEntityRegistry();
    const location = useLocation();
    const params = QueryString.parse(location.search, { arrayFormat: 'comma' });
    const paramsQuery = (params?.query as string) || undefined;
    const [query, setQuery] = useState<undefined | string>(undefined);
    useEffect(() => setQuery(paramsQuery), [paramsQuery]);

    const {
        config: { policiesConfig },
    } = useAppConfig();

    // Policy list paging.
    const [page, setPage] = useState(1);
    const pageSize = DEFAULT_PAGE_SIZE;
    const start = (page - 1) * pageSize;

    // Controls whether the editing and details view modals are active.
    const [showPolicyBuilderModal, setShowPolicyBuilderModal] = useState(false);
    const [showViewPolicyModal, setShowViewPolicyModal] = useState(false);

    // Focused policy represents a policy being actively viewed, edited, created via a popup modal.
    const [focusPolicyUrn, setFocusPolicyUrn] = useState<undefined | string>(undefined);
    const [focusPolicy, setFocusPolicy] = useState<Omit<Policy, 'urn'>>(EMPTY_POLICY);

    // Construct privileges
    const platformPrivileges = policiesConfig?.platformPrivileges || [];
    const resourcePrivileges = policiesConfig?.resourcePrivileges || [];

    const {
        loading: policiesLoading,
        error: policiesError,
        data: policiesData,
        refetch: policiesRefetch,
        networkStatus,
    } = useListPoliciesQuery({
        fetchPolicy: 'no-cache',
        notifyOnNetworkStatusChange: true,
        variables: {
            input: {
                start,
                count: pageSize,
                query: query || '',
            },
        },
    });

    // NetworkStatus: 1=loading, 4=refetch, 7=ready
    const isRefetching = networkStatus === 4;
    const isLoadingOrRefetching = policiesLoading || isRefetching;

    // Any time a policy is removed, edited, or created, refetch the list.
    const [createPolicy, { error: createPolicyError }] = useCreatePolicyMutation();

    const [updatePolicy, { error: updatePolicyError }] = useUpdatePolicyMutation();

    const [deletePolicy, { error: deletePolicyError }] = useDeletePolicyMutation();

    const policyName = useRef<string>();

    const updateError = createPolicyError || updatePolicyError || deletePolicyError;

    const totalPolicies = policiesData?.listPolicies?.total || 0;
    const policies = useMemo(() => policiesData?.listPolicies?.policies || [], [policiesData]);

    const onChangePage = (newPage: number) => {
        scrollToTop();
        setPage(newPage);
    };

    const onClickNewPolicy = () => {
        setFocusPolicyUrn(undefined);
        setFocusPolicy(EMPTY_POLICY);
        policyName.current = undefined;
        setShowPolicyBuilderModal(true);
    };

    const onClosePolicyBuilder = () => {
        setFocusPolicyUrn(undefined);
        setFocusPolicy(EMPTY_POLICY);
        policyName.current = undefined;
        setShowPolicyBuilderModal(false);
    };

    const getPrivilegeNames = (policy: Omit<Policy, 'urn'>) => {
        let privileges: PrivilegeOptionType[] = [];
        if (policy?.type === PolicyType.Platform) {
            privileges = platformPrivileges
                .filter((platformPrivilege) => policy.privileges.includes(platformPrivilege.type))
                .map((platformPrivilege) => {
                    return { type: platformPrivilege.type, name: platformPrivilege.displayName };
                });
        } else {
            const allResourcePriviliges = resourcePrivileges.find(
                (resourcePrivilege) => resourcePrivilege.resourceType === 'all',
            );
            privileges =
                allResourcePriviliges?.privileges
                    .filter((resourcePrivilege) => policy.privileges.includes(resourcePrivilege.type))
                    .map((b) => {
                        return { type: b.type, name: b.displayName };
                    }) || [];
        }
        return privileges;
    };

    const onViewPolicy = (policy: Policy) => {
        setShowViewPolicyModal(true);
        setFocusPolicyUrn(policy?.urn);
        policyName.current = policy?.name;
        setFocusPolicy({ ...policy });
    };

    const onCancelViewPolicy = () => {
        setShowViewPolicyModal(false);
        setFocusPolicy(EMPTY_POLICY);
        policyName.current = undefined;
        setFocusPolicyUrn(undefined);
    };

    const onEditPolicy = (policy: Policy) => {
        setShowPolicyBuilderModal(true);
        setFocusPolicyUrn(policy?.urn);
        policyName.current = policy?.name;
        setFocusPolicy({ ...policy });
    };

    // On Delete Policy handler
    const onRemovePolicy = (policy: Policy) => {
        Modal.confirm({
            title: t('Delete {{name}}', { name: policy?.name }),
            content: t('Are you sure you want to delete this policy?'),
            onOk() {
                deletePolicy({ variables: { urn: policy?.urn as string } }); // There must be a focus policy urn.
                analytics.event({
                    type: EventType.DeleteEntityEvent,
                    entityUrn: policy?.urn,
                    entityType: EntityType.DatahubPolicy,
                });
                message.success(t('Successfully deleted policy.'));
                setTimeout(() => {
                    policiesRefetch();
                }, 3000);
                onCancelViewPolicy();
            },
            onCancel() {},
            okText: t('Yes'),
            cancelText: t('Cancel'),
            maskClosable: true,
            centered: true,
            cancelButtonProps: { type: 'default', className: 'ant-btn-default' },
            style: {
                maxWidth: 400,
            },
            okButtonProps: {
                type: undefined,
                danger: true,
            },
            closable: true,
        });
    };

    // On Activate and deactivate Policy handler
    const onToggleActiveDuplicate = (policy: Policy) => {
        const newState = policy?.state === PolicyState.Active ? PolicyState.Inactive : PolicyState.Active;
        const newPolicy = {
            ...policy,
            state: newState,
        };
        updatePolicy({
            variables: {
                urn: policy?.urn as string, // There must be a focus policy urn.
                input: toPolicyInput(newPolicy),
            },
        });
        message.success(
            t('Successfully {{state}} policy.', {
                state: newState === PolicyState.Active ? t('activated') : t('deactivated'),
            }),
        );
        setTimeout(() => {
            policiesRefetch();
        }, 3000);
        setShowViewPolicyModal(false);
    };

    // On Add/Update Policy handler
    const onSavePolicy = async (savePolicy: Omit<Policy, 'urn'>) => {
        try {
            if (focusPolicyUrn) {
                // If there's an URN associated with the focused policy, then we are editing an existing policy.
                await updatePolicy({ variables: { urn: focusPolicyUrn, input: toPolicyInput(savePolicy) } });
                analytics.event({
                    type: EventType.UpdatePolicyEvent,
                    policyUrn: focusPolicyUrn,
                });
            } else {
                // If there's no URN associated with the focused policy, then we are creating.
                await createPolicy({ variables: { input: toPolicyInput(savePolicy) } });
                analytics.event({
                    type: EventType.CreatePolicyEvent,
                });
            }
            message.success(t('Successfully saved policy.'));
            onClosePolicyBuilder();
            await policiesRefetch();
        } catch (error) {
            message.error(t('Failed to save policy. An unexpected error occurred.'));
        }
    };

    // const cardTitle = `${policies.length} Policies`;
    const tableColumns = [
        {
            title: t('Name'),
            dataIndex: 'name',
            key: 'name',
            render: (_, record: any) => {
                return (
                    <PolicyName
                        onClick={() => onViewPolicy(record.policy)}
                        style={{ color: record?.editable ? '#4C4E54' : '#8F9094' }}
                    >
                        {record?.name}
                    </PolicyName>
                );
            },
        },
        {
            title: t('Type'),
            dataIndex: 'type',
            key: 'type',
            render: (type: string) => {
                const policyType = type?.charAt(0)?.toUpperCase() + type?.slice(1)?.toLowerCase();
                return <PoliciesType className="pri-tag-outline">{t(policyType)}</PoliciesType>;
            },
        },
        {
            title: t('Description'),
            dataIndex: 'description',
            key: 'description',
            render: (description: string) => description || '',
        },
        {
            title: t('Actors'),
            dataIndex: 'actors',
            key: 'actors',
            render: (_, record: any) => {
                return (
                    <>
                        <AvatarsGroup
                            users={record?.resolvedUsers}
                            groups={record?.resolvedGroups}
                            entityRegistry={entityRegistry}
                            maxCount={3}
                            size={28}
                        />
                        {record?.allUsers ? <ActorTag>{t('All Users')}</ActorTag> : null}
                        {record?.allGroups ? <ActorTag>{t('All Groups')}</ActorTag> : null}
                        {record?.resourceOwners ? <ActorTag>{t('All Owners')}</ActorTag> : null}
                    </>
                );
            },
        },
        {
            title: t('State'),
            dataIndex: 'state',
            key: 'state',
            render: (state: string) => {
                const isActive = state === PolicyState.Active;
                return <StateTag className={isActive ? 'green' : 'red'}>{t(state.toLowerCase())}</StateTag>;
            },
        },
        {
            title: <TableActionHeader>{t('Actions')}</TableActionHeader>,
            dataIndex: '',
            key: 'x',
            render: (_, record: any) => (
                <ActionButtonContainer>
                    <EditPolicyButton
                        disabled={!record?.editable}
                        onClick={() => onEditPolicy(record?.policy)}
                        type="default"
                        className="ant-btn-default"
                    >
                        {t('Edit')}
                    </EditPolicyButton>
                    {record?.state === PolicyState.Active ? (
                        <ToggleActiveButton
                            disabled={!record?.editable}
                            onClick={() => {
                                onToggleActiveDuplicate(record?.policy);
                                analytics.event({
                                    type: EventType.DeactivatePolicyEvent,
                                    policyUrn: record?.policy?.urn,
                                });
                            }}
                            type="default"
                            className="ant-btn-default"
                        >
                            {t('Deactivate')}
                        </ToggleActiveButton>
                    ) : (
                        <ToggleActiveButton
                            disabled={!record?.editable}
                            onClick={() => {
                                onToggleActiveDuplicate(record?.policy);
                                analytics.event({
                                    type: EventType.ActivatePolicyEvent,
                                    policyUrn: record?.policy?.urn,
                                });
                            }}
                            type="primary"
                        >
                            {t('Activate')}
                        </ToggleActiveButton>
                    )}
                    <Button
                        disabled={!record?.editable}
                        onClick={() => onRemovePolicy(record?.policy)}
                        type="text"
                        shape="circle"
                    >
                        <DeleteFilled style={{ fontSize: '16px', color: '#D96758' }} />
                    </Button>
                </ActionButtonContainer>
            ),
        },
    ];

    const tableData = policies?.map((policy) => ({
        allGroups: policy?.actors?.allGroups,
        allUsers: policy?.actors?.allUsers,
        resourceOwners: policy?.actors?.resourceOwners,
        description: policy?.description,
        editable: policy?.editable,
        name: policy?.name,
        privileges: policy?.privileges,
        policy,
        resolvedGroups: policy?.actors?.resolvedGroups,
        resolvedUsers: policy?.actors?.resolvedUsers,
        resources: policy?.resources,
        state: policy?.state,
        type: policy?.type,
        urn: policy?.urn,
    }));

    return (
        <PageContainer tabIndex={-1}>
            {policiesError && (
                <Message type="error" content={`${t('Failed to load policies!')} ${policiesError.message}`} />
            )}
            {updateError && message.error(t('Failed to update policies. An unexpected error occurred.'))}
            <SourceContainer>
                <TabToolbar>
                    <div>
                        <Button
                            type="text"
                            onClick={onClickNewPolicy}
                            data-testid="add-policy-button"
                            aria-label={t('Create New Policy')}
                        >
                            <PlusOutlined aria-hidden="true" /> {t('Create New Policy')}
                        </Button>
                    </div>
                    <SearchBar
                        initialQuery={query || ''}
                        placeholderText={t('Search Policies')}
                        suggestions={[]}
                        style={{
                            maxWidth: 270,
                            padding: 0,
                        }}
                        inputStyle={{
                            height: 32,
                            fontSize: 14,
                        }}
                        onSearch={() => null}
                        onQueryChange={(q) => {
                            setQuery(q);
                            setPage(1);
                        }}
                        entityRegistry={entityRegistry}
                        hideRecommendations
                    />
                </TabToolbar>
                {isLoadingOrRefetching && <ComponentSpinner />}
                <StyledTable
                    columns={tableColumns}
                    dataSource={isLoadingOrRefetching ? [] : tableData}
                    rowKey="urn"
                    locale={{
                        emptyText: (
                            <Empty
                                description={isRefetching ? t('Refetching policies') : t('No Policies!')}
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                            />
                        ),
                    }}
                    pagination={false}
                />
            </SourceContainer>
            <PaginationContainer>
                <Pagination
                    style={{ margin: 40 }}
                    current={page}
                    pageSize={pageSize}
                    total={totalPolicies}
                    showLessItems
                    onChange={onChangePage}
                    showSizeChanger={false}
                />
            </PaginationContainer>
            {showPolicyBuilderModal && (
                <PolicyBuilderModal
                    policy={focusPolicy || EMPTY_POLICY}
                    policyName={policyName?.current}
                    setPolicy={setFocusPolicy}
                    visible={showPolicyBuilderModal}
                    onClose={onClosePolicyBuilder}
                    onSave={onSavePolicy}
                />
            )}
            {showViewPolicyModal && (
                <PolicyDetailsModal
                    policy={focusPolicy}
                    visible={showViewPolicyModal}
                    onClose={onCancelViewPolicy}
                    privileges={getPrivilegeNames(focusPolicy)}
                />
            )}
        </PageContainer>
    );
};
